import { isNull, isUndefined } from 'lodash';
import APIEndpoints from '../utils/apiEndpoints';
import AbstractService from './abstractService';

// Singleton instance
let instance = null;

const _SERVICE_UNAVAILABLE_ = new Error({ message : 'Service Unavailable' });

export default class ClientService extends AbstractService {
	static setup(baseURL, options) {
		instance = new ClientService(baseURL, options);
	}

	static getInstance() {
		return instance;
	}

	constructor(baseURL, options) {
		super(baseURL, options);
		this.endpoints = {
			organizations  : APIEndpoints.organizations,
			clientOverview : APIEndpoints.clientOverview,
			clients        : APIEndpoints.clients,
		};
	}

	static getAll(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getAll(...params);
	}

	static uploadImage(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._uploadImage(...params);
	}

	static getOverview(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getOverview(...params);
	}

	static getById(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getById(...params);
	}

	static getLocations(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getLocations(...params);
	}

	static search(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._search(...params);
	}

	static save(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._save(...params);
	}

	static delete(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._delete(...params);
	}

	static exportReport(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._exportReport(...params);
	}

	static deleteImage(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._deleteImage(...params);
	}

	static getPosts(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getPosts(...params);
	}

	static uploadClientImage(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._uploadClientImage(...params);
	}

	static updateOrganizationFees(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._updateOrganizationFees(...params);
	}

	static inviteClient(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._inviteClient(...params);
	}

	static uploadTaxExemptionDocument(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._uploadTaxExemptionDocument(...params);
	}

	_getAll(queryParams) {
		return this.get({
			endpoint : this.endpoints.clients,
			queryParams,
		});
	}

	_uploadImage({ file }) {
		const data = new FormData();
		data.append('file', file);

		return this.post({
			endpoint : `${this.endpoints.organizations}/picture`,
			data,
		});
	}

	_getOverview({
		clientId,
		startDateRange,
		endDateRange,
		includeAll,
		clientSupervisorContactId = '',
	}) {
		return this.get({
			endpoint    : this.endpoints.clientOverview,
			queryParams : {
				clientId,
				startDateRange,
				endDateRange,
				includeAll,
				clientSupervisorContactId,
			},
		});
	}

	_getById(id, queryParams) {
		return this.get({
			endpoint : `${this.endpoints.clients}/${id}`,
			queryParams,
		});
	}

	_getLocations({
		id,
		minimalData,
		includeStats,
	}) {
		return this.get({
			endpoint    : `${this.endpoints.clients}/${id}/locations`,
			queryParams : {
				minimalData,
				includeStats,
			},
		});
	}

	_search({
		pageNumber,
		displayCount,
		includeDeleted,
		name,
		contactFirstName : primaryContactFirstName,
		contactLastName : primaryContactLastName,
		contactEmail : primaryContactEmail,
	}) {
		return this.get({
			endpoint    : this.endpoints.clients,
			queryParams : {
				pageNumber,
				displayCount,
				includeDeleted,
				name,
				primaryContactFirstName,
				primaryContactLastName,
				primaryContactEmail,
			},
		});
	}

	_save(data, queryParams) {
		const isCreating = isUndefined(data.id);
		const endpoint = `${this.endpoints.clients}${!isCreating ? `/${data.id}` : ''}`;

		return this.request({
			endpoint,
			method : isCreating ? 'POST' : 'PUT',
			data,
			queryParams,
		});
	}

	_delete(id) {
		return this.del({
			endpoint : `${this.endpoints.clients}/${id}`,
		});
	}

	_exportReport({
		type, data,
	}) {
		return this.post({
			endpoint    : `${this.endpoints.clientOverview}/ExportReport`,
			queryParams : { type },
			data,
		});
	}

	_deleteImage() {
		return this.del({
			endpoint : `${this.endpoints.organizations}/picture`,
		});
	}

	_getPosts({
		id,
		pagingToken,
		displayCount,
		pageNumber,
	}) {
		return this.get({
			endpoint    : `${this.endpoints.clients}/${id}/posts`,
			queryParams : {
				pagingToken,
				displayCount,
				pageNumber,
			},
		});
	}

	_uploadClientImage({ id, file }) {
		const data = new FormData();
		data.append('file', file);

		return this.post({
			endpoint : `${this.endpoints.clients}/${id}/picture`,
			data,
		});
	}

	_updateOrganizationFees({ organizationFeeModelList }) {
		return this.put({
			endpoint : `${this.endpoints.organizations}/organizationsfee`,
			data     : { organizationFeeModelList },
		});
	}

	_inviteClient(data) {
		return this.post({
			endpoint : 'clientinvitations',
			data,
		});
	}

	_uploadTaxExemptionDocument(id, fileData) {
		const data = new FormData();
		data.append('file', fileData);

		return this.post({
			endpoint : `${this.endpoints.clients}/${id}/taxexemption`,
			data,
		});
	}
}
